import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Bell, User, Lock, Mail } from 'lucide-react';

export default function Profile() {
  const { user, token, updateUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState('account');
  const [loading, setLoading] = useState(false);
  
  // Account form
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  
  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Notification preferences
  const [preferences, setPreferences] = useState({
    email_badge_earned: true,
    email_milestone_reached: true,
    email_course_completed: true,
    email_discussion_reply: true,
    email_solution_marked: true,
    email_weekly_progress: true,
    email_marketing: false,
  });

  useEffect(() => {
    if (activeTab === 'notifications') {
      loadPreferences();
    }
  }, [activeTab]);

  const loadPreferences = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}/api/notification-preferences`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreferences(response.data);
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}/api/auth/profile`,
        { name, email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update the user in the auth store with the new data from the backend
      if (response.data.user) {
        updateUser(response.data.user);
      }
      
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}/api/auth/password`,
        { 
          current_password: currentPassword, 
          new_password: newPassword,
          new_password_confirmation: confirmPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePreference = async (key) => {
    const newPreferences = {
      ...preferences,
      [key]: !preferences[key],
    };
    
    setPreferences(newPreferences);
    
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}/api/notification-preferences`,
        { [key]: newPreferences[key] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Preferences updated');
    } catch (error) {
      // Revert on error
      setPreferences(preferences);
      toast.error('Failed to update preferences');
    }
  };

  const tabs = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  const notificationOptions = [
    {
      key: 'email_badge_earned',
      title: 'Badge Earned',
      description: 'Get notified when you earn a new badge',
      icon: '🏆',
    },
    {
      key: 'email_milestone_reached',
      title: 'Milestone Reached',
      description: 'Celebrate when you hit 25%, 50%, or 75% completion',
      icon: '🎯',
    },
    {
      key: 'email_course_completed',
      title: 'Course Completed',
      description: 'Receive your certificate and completion summary',
      icon: '🎓',
    },
    {
      key: 'email_discussion_reply',
      title: 'Discussion Replies',
      description: 'Get notified when someone replies to your discussion',
      icon: '💬',
    },
    {
      key: 'email_solution_marked',
      title: 'Solution Marked',
      description: 'Know when your answer is marked as a solution',
      icon: '✅',
    },
    {
      key: 'email_weekly_progress',
      title: 'Weekly Progress Report',
      description: 'Receive a summary of your weekly learning activity',
      icon: '📊',
    },
    {
      key: 'email_marketing',
      title: 'Marketing & Updates',
      description: 'Occasional emails about new courses and features',
      icon: '📧',
    },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Profile Settings</h1>
      <p className="text-gray-600 mb-8">Manage your account settings and preferences</p>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-5 w-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Account Tab */}
      {activeTab === 'account' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Account Information</h2>
          
          <form onSubmit={handleUpdateProfile} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Change Password</h2>
          
          <form onSubmit={handleChangePassword} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                minLength={8}
                required
              />
              <p className="text-sm text-gray-500 mt-1">Must be at least 8 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="h-6 w-6 text-indigo-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Email Notifications</h2>
              <p className="text-sm text-gray-600">Choose which emails you'd like to receive</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {notificationOptions.map((option) => (
              <div
                key={option.key}
                className="flex items-start justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-start gap-3 flex-1">
                  <span className="text-2xl">{option.icon}</span>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{option.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                  </div>
                </div>
                
                <button
                  onClick={() => handleTogglePreference(option.key)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    preferences[option.key] ? 'bg-indigo-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences[option.key] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> Email notifications are sent to <strong>{user?.email}</strong>. 
              Update your email in the Account tab if needed.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
